import React from 'react';
import { PlusIcon, Trash2Icon, ZapIcon } from 'lucide-react';
import { FormConfig, FormData, RuleOperator, VisibilityRule } from '../../types';
import { conditionMet, createRuleId, describeRule, fieldCatalog } from '../../utils/config';
import { Select, Toggle } from './AdminControls';
interface RulesTabProps {
  config: FormConfig;
  values: FormData;
  onChange: (config: FormConfig) => void;
}
const OPERATORS: {
  value: RuleOperator;
  label: string;
}[] = [{
  value: 'equals',
  label: 'is'
}, {
  value: 'notEquals',
  label: 'is not'
}, {
  value: 'isEmpty',
  label: 'is empty'
}, {
  value: 'isNotEmpty',
  label: 'has a value'
}];
export function RulesTab({
  config,
  values,
  onChange
}: RulesTabProps) {
  const fieldOptions = Object.values(fieldCatalog).map(f => ({
    value: f.id,
    label: f.label
  })).sort((a, b) => a.label.localeCompare(b.label));
  const updateRule = (id: string, patch: Partial<VisibilityRule>) => {
    onChange({
      ...config,
      rules: config.rules.map(r => r.id === id ? {
        ...r,
        ...patch
      } : r)
    });
  };
  const addRule = () => {
    const rule: VisibilityRule = {
      id: createRuleId(),
      enabled: true,
      targetFieldId: fieldOptions[0]?.value ?? '',
      action: 'hide',
      conditionFieldId: fieldOptions[0]?.value ?? '',
      operator: 'isEmpty',
      conditionValue: ''
    };
    onChange({
      ...config,
      rules: [...config.rules, rule]
    });
  };
  const deleteRule = (id: string) => {
    onChange({
      ...config,
      rules: config.rules.filter(r => r.id !== id)
    });
  };
  return <div className="space-y-3" data-id="element-33" data-mp="3hxwv0nhjoqvn">
      <p className="text-xs leading-relaxed text-slate-500" data-id="element-34" data-mp="3mz2quu6i4u9e">
        Rules run against the live record. When a condition matches, the target field is shown or
        hidden automatically — overriding the section defaults.
      </p>

      {config.rules.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 py-10 text-center" data-id="element-35" data-mp="2z9rxvyve845t">
          <ZapIcon className="mx-auto mb-2 h-5 w-5 text-slate-300" data-id="element-36" data-mp="2r92fnuumxmck" />
          <p className="text-sm text-slate-400" data-id="element-37" data-mp="2r797uc8acw2z">No visibility rules yet.</p>
        </div>}

      {config.rules.map(rule => {
      const active = rule.enabled && conditionMet(rule, values);
      const conditionField = fieldCatalog[rule.conditionFieldId];
      const needsValue = rule.operator === 'equals' || rule.operator === 'notEquals';
      return <div key={rule.id} className={`rounded-xl border bg-white p-3 ${active ? 'border-blue-300 ring-1 ring-blue-100' : 'border-slate-200'}`} data-id="element-38" data-mp="28azkszfeov6u">
            <div className="mb-3 flex items-start gap-2" data-id="element-39" data-mp="2i9ywyrzdr8yl">
              <p className="min-w-0 flex-1 text-xs font-medium leading-relaxed text-slate-700" data-id="element-40" data-mp="2d8rosrtbs7re">
                {describeRule(rule)}
              </p>
              {active && <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-600" data-id="element-41" data-mp="3oogcjleelat9">
                  Active
                </span>}
              <button type="button" aria-label="Delete rule" onClick={() => deleteRule(rule.id)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-42" data-mp="1ffs1fl4mehwk">
                <Trash2Icon className="h-3.5 w-3.5" data-id="element-43" data-mp="1u463vuy4k4cl" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2" data-id="element-44" data-mp="1y6syg3m6k3dh">
              <Select label="Action" value={rule.action} onChange={v => updateRule(rule.id, {
            action: v as 'hide' | 'show'
          })} options={[{
            value: 'hide',
            label: 'Hide field'
          }, {
            value: 'show',
            label: 'Show field'
          }]} data-id="element-45" data-mp="1y5hsbx59ijj7" />
              <Select label="Target field" value={rule.targetFieldId} onChange={v => updateRule(rule.id, {
            targetFieldId: v
          })} options={fieldOptions} data-id="element-46" data-mp="2huihnmg1wvb3" />
              <Select label="When field" value={rule.conditionFieldId} onChange={v => updateRule(rule.id, {
            conditionFieldId: v
          })} options={fieldOptions} data-id="element-47" data-mp="o7t8v2nazcqd" />
              <Select label="Condition" value={rule.operator} onChange={v => updateRule(rule.id, {
            operator: v as RuleOperator
          })} options={OPERATORS} data-id="element-48" data-mp="1byxhu3uwzzem" />
              {needsValue && <div className="col-span-2" data-id="element-49" data-mp="134smp88r01ta">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-50" data-mp="y7si71rjr74a">
                    Value
                  </span>
                  {conditionField?.options?.length ? <Select value={rule.conditionValue} ariaLabel="Condition value" onChange={v => updateRule(rule.id, {
              conditionValue: v
            })} options={[{
              value: '',
              label: '— Select —'
            }, ...conditionField.options.map(o => ({
              value: o,
              label: o
            }))]} data-id="element-51" data-mp="1qv6h35et2cr1" /> : <input value={rule.conditionValue} aria-label="Condition value" onChange={e => updateRule(rule.id, {
              conditionValue: e.target.value
            })} placeholder="Enter a value" className="w-full rounded-lg border border-slate-300 px-2.5 py-2 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-52" data-mp="2h6867rdde05v" />}
                </div>}
            </div>

            <div className="mt-1 border-t border-slate-100" data-id="element-53" data-mp="34tgfslhtzvdp">
              <Toggle label="Rule enabled" checked={rule.enabled} onChange={v => updateRule(rule.id, {
            enabled: v
          })} data-id="element-54" data-mp="14u2z28vsttcv" />
            </div>
          </div>;
    })}

      <button type="button" onClick={addRule} className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-600" data-id="element-55" data-mp="1el7h5hgrtubx">
        <PlusIcon className="h-4 w-4" data-id="element-56" data-mp="1jkk37zztixdg" />
        Add rule
      </button>
    </div>;
}